import { FC, Ref, useState } from 'react';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { useCartQuery } from '@telialt/core-eshop/src/hooks/queries/useCartQuery';
import { useIsProductPageMutating } from '@telialt/core-eshop/src/hooks/useIsProductPageMutating';
import { HybrisOrderCart, HybrisOrderCartProps } from '@telialt/core-eshop/src/HybrisOrderCart/HybrisOrderCart';
import { trackAddToCartGA } from '@telialt/gtm-tracking/src/events';
import { Cart } from '@telialt/models/src/EshopModel';
// TODO: uncomment with LTCONT-16063
// import { selectPossibleRatePlans, selectPossibleSapRatePlans } from '@telialt/utils/src/eshopSelectors';
import { isEmpty } from '@telialt/utils/src/helpers';
import { TeliaSegment, useSegment } from '@telialt/utils/src/segment';
import { useUserData } from '@telialt/utils/src/SSO/useUserData';

type Props = {
    cartTotalsRef?: Ref<HTMLElement>;
};

const getNextStepUrl = (segment: TeliaSegment, isUserAuthenticated: boolean, cart: Cart) => {
    const cartGuid = cart.guid;

    if (segment === TeliaSegment.B2b) {
        return `/verslui/prekes/uzsakymas-priedai?cartGuid=${cartGuid}`;
    }
    // TODO: uncomment with LTCONT-16063 when correct routing to other pages should be enabled
    // if (!isUserAuthenticated) {
    //     return '/privatiems/pirkimas/prisijungimas';
    // }
    // const possibleSapRatePlans = selectPossibleSapRatePlans(cart);
    // if (isUserAuthenticated && !!possibleSapRatePlans?.length) {
    //     return '/privatiems/pirkimas/planas';
    // }

    return `/privatiems/prekes/uzsakymas-priedai?cartGuid=${cartGuid}`;
};

export const ProductOrderCart: FC<Props> = ({ cartTotalsRef }) => {
    const { t } = useTranslations();
    const segment = useSegment();
    const { cart } = useCartQuery();
    const { userData } = useUserData();

    const [isLoading, setIsLoading] = useState(false);

    const isProductPageMutating = useIsProductPageMutating();

    // TODO: uncomment with LTCONT-16063
    // const possibleSapRatePlans = selectPossibleSapRatePlans(cart);
    // const possibleRatePlans = selectPossibleRatePlans(cart);
    // const showRatePlanRelatedEntries = !!possibleRatePlans?.length && !possibleSapRatePlans?.length;

    // TODO: uncomment with LTCONT-16063. For now "Tęsti" should be always a label
    // const actionText =
    //     !!possibleSapRatePlans?.length && segment === TeliaSegment.B2c
    //         ? t['eshop.order_cart.primary_action.text_rate_plan']
    //         : t['eshop.order_cart.primary_action.text'];
    const actionText = t['eshop.order_cart.primary_action.text'];

    const isActionDisabled = isProductPageMutating;

    const nextStepUrl = getNextStepUrl(segment, !!userData, cart);

    const handleGoToNextStep = () => {
        if (isActionDisabled || isLoading) {
            return;
        }

        setIsLoading(true);
        if (!isEmpty(cart?.entries)) {
            trackAddToCartGA({ source: 'productPage', data: { cart } });
        }

        window.location.assign(nextStepUrl);
    };

    const header: HybrisOrderCartProps['header'] = {
        expandable: 'ALWAYS',
        isDefaultExpanded: true,
    };
    const totals: HybrisOrderCartProps['totals'] = {
        // isTotalMonthlyPriceWithoutRatePlan: !showRatePlanRelatedEntries, // TODO: uncomment with LTCONT-16063
        cartTotalsRef,
    };
    const action: HybrisOrderCartProps['action'] = {
        onClick: handleGoToNextStep,
        hasArrow: true,
        isDisabled: isActionDisabled,
        isLoading,
        text: actionText,
    };

    return (
        <HybrisOrderCart
            header={header}
            totals={totals}
            action={action}
            showValuePrompts={true}
            // showRatePlanRelatedEntries={showRatePlanRelatedEntries} // TODO: uncomment with LTCONT-16063
        />
    );
};
